import { TestRunVersionProgress } from "src/api/endpoints";
import { FlowRunnerResultV2, GraphRunResultType } from "src/api/types";

/**
 * Keeps the breakdown bar at a stable width while every
 * bucket is still empty
 */
export const PLACEHOLDER_PROGRESS: TestRunVersionProgress = {
  total: 100,
  successful: 0,
  failed: 0,
  ignored: 0,
  output_mismatch: 0,
};

/**
 * Derives progress from a finished version's execution results, for the
 * window after a run completes but before the API drops `progress`.
 */
export const executionResultsToProgress = (
  result: FlowRunnerResultV2,
): TestRunVersionProgress | null => {
  if (result.type === GraphRunResultType.ERROR) {
    return null;
  }
  return {
    total: result.nodes.output.total_count,
    successful: result.nodes.output.success_count,
    failed: result.nodes.output.failure_count,
    ignored: result.nodes.output.ignored_count,
    output_mismatch: result.nodes.output.expected_output_mismatch_count ?? 0,
  };
};

export const TEST_RUN_STATUS_COLORS = {
  successful: "green",
  output_mismatch: "yellow",
  failed: "red",
  ignored: "gray",
  waiting: "neutral",
} as const;

export type TestRunStatusColor =
  (typeof TEST_RUN_STATUS_COLORS)[keyof typeof TEST_RUN_STATUS_COLORS];

/**
 * Splits progress into `Breakdown` bars, with the unprocessed remainder as a
 * trailing "waiting" bar so the bar's width tracks completion.
 */
export const testRunBreakdownBars = (
  progress: TestRunVersionProgress,
): { color: TestRunStatusColor; count: number }[] => [
  { color: TEST_RUN_STATUS_COLORS.successful, count: progress.successful },
  { color: TEST_RUN_STATUS_COLORS.failed, count: progress.failed },
  {
    color: TEST_RUN_STATUS_COLORS.output_mismatch,
    count: progress.output_mismatch,
  },
  { color: TEST_RUN_STATUS_COLORS.ignored, count: progress.ignored },
  {
    color: TEST_RUN_STATUS_COLORS.waiting,
    count:
      progress.total -
      progress.successful -
      progress.failed -
      progress.ignored -
      progress.output_mismatch,
  },
];
